// ABOUTME: Pure transform — converts the Animation Developer Specification JSON into the internal
// ABOUTME: reel manifest schema (move/hold/exit segments, beats, chapter ids). No React, no DOM.

/**
 * Maps animation spec section ids to internal chapter ids.
 * CAROUSSEL → collection (the variant/colour showcase section).
 * CAROUSSEL_APPROACH → collection_approach (dual-outro combined cut, 2026-08-28): the
 * strip that match-cuts from the lit lineup hold back onto itself and dims down to the
 * darkened boundary the variant slots open on. It is a route CORRIDOR, not a place —
 * the planner drives through it on slot entries (manifest.approachChapter); it is
 * deliberately absent from the backend's CHAPTER_IDS so the agent can never navigate
 * to it, and the player keeps it out of go_back history.
 * CAROUSSEL_ROLL → collection_roll (SPEC-61 D-6): the mobile-only freeze that waits for a
 * colour pick, following the collection_approach precedent — a client-only corridor id the
 * backend's chapter set never carries, absent on every desktop drop.
 */
const SECTION_TO_CHAPTER_ID = {
  IDLE: 'idle',
  '360': 'watch_360',
  DIAL: 'dial',
  MOVEMENT: 'movement',
  EXPLODED: 'exploded',
  CROWN: 'crown',
  CAROUSSEL: 'collection',
  CAROUSSEL_APPROACH: 'collection_approach',
  CAROUSSEL_ROLL: 'collection_roll',
};

/**
 * Whether a spec section id names a MAIN-reel section. Object-key lookup coerces numbers,
 * so a drop that ships 360 as a JSON NUMBER still lands on watch_360 — it must never fall
 * into the variant plane just because its id happens to be an integer.
 */
export function isMainSectionId(id) {
  return SECTION_TO_CHAPTER_ID[id] !== undefined;
}

// Chapters that must not sit frozen once their `None` action plays out: they settle into
// idle's up/down breath instead (team review 2026-08-27 — "360 shouldn't freeze at its hold,
// it should breathe like idle"). Borrowing idle's frames is only sound because every section
// starts and ends on the shared neutral pose, so the end of the rotation and the start of
// idle's loop are the same picture.
const BORROWS_IDLE_BREATH = new Set(['watch_360']);

const CHAPTER_TITLES = {
  idle: 'Vesper at Rest',
  watch_360: '360 Degree View',
  dial: 'The Dial',
  movement: 'The Movement',
  exploded: 'Exploded View',
  crown: 'The Crown',
  collection: 'The Collection',
  collection_approach: 'The Collection — Approach', // never displayed: no captions, transit-only
  collection_relight: 'The Collection — Relight',   // ditto — the approach's mirror strip
  collection_roll: 'The Collection — Roll',         // ditto — SPEC-61, the mobile colour-pick freeze
};

/**
 * SPEC-61 D-4 — the frame count a spec's OWN sections actually address, floored at the
 * declared `total_frames`. F5: the mobile pair under-declares by 490 frames (declared 3353,
 * `CAROUSSEL_ROLL` addresses 3842) — trusting the declared value alone throws every roll
 * segment through reelSchema's duration check. Scanning every frame-like field rather than
 * trusting one section keeps this correct for any future drop, not just the one it was
 * written for. F6: on every desktop pair every section stays inside `total_frames`, so this
 * is an identity there — the derivation is provably a no-op on the shipped desktop fixture.
 */
const FRAME_FIELDS = ['start_frame', 'end_frame', 'freeze_frame', 'jump_to_frame', 'loop_start', 'loop_end'];

// Code review finding (2026-09-15): useChapterManifest.js's load() calls this once via
// buildManifest and once more via extractEmbeddedVariantSections, both against the SAME
// parsed toolSpec object — without memoizing, the under-declared-frames warning (§4.6)
// printed twice per mobile manifest load, once from each call site. A WeakMap keyed on the
// object identity is scoped to exactly one load (a fresh `toolSpec` is parsed every fetch,
// so nothing survives across loads to grow unbounded, and the entry is GC-able once the
// closure that holds `toolSpec` returns) — cheaper and smaller than threading a precomputed
// value through both call sites' signatures.
const totalFramesCache = new WeakMap();

export function effectiveTotalFrames(toolSpec) {
  if (toolSpec && typeof toolSpec === 'object' && totalFramesCache.has(toolSpec)) {
    return totalFramesCache.get(toolSpec);
  }
  const declared = toolSpec?.total_frames;
  let highest = declared;
  const sections = Array.isArray(toolSpec?.sections) ? toolSpec.sections : [];
  for (const section of sections) {
    for (const field of FRAME_FIELDS) {
      const value = section[field];
      if (Number.isFinite(value) && value > highest) highest = value;
    }
  }
  const relight = toolSpec?.relight;
  if (relight && Number.isFinite(relight.end_frame) && relight.end_frame > highest) {
    highest = relight.end_frame;
  }
  if (highest > declared) {
    console.warn(
      `[SPEC-61] spec total_frames ${declared} is below the last declared frame ${highest}`
      + ` — using ${highest} (fix the asset)`,
    );
  }
  if (toolSpec && typeof toolSpec === 'object') totalFramesCache.set(toolSpec, highest);
  return highest;
}

/**
 * Build the internal reel manifest from the Animation Developer Specification JSON.
 *
 * @param {object} toolSpec  - Raw parsed JSON from S001_PB_V004.json.
 * @param {object} editorial - Optional overlay keyed by chapter id; may add or override:
 *   title, beats, captions, deixis, allowedTransitions.
 *   Fields in the overlay are merged on top of the auto-derived values.
 * @param {object} [options]
 * @param {string} [options.media] - The media filename every chapter of THIS spec plays from
 *   (SPEC-34 D13: the hardcode became a parameter so a second spec can transform against its
 *   own file; the default keeps main-spec behaviour byte-identical).
 * @returns {object} Manifest conforming to the reelSchema.js contract.
 */
export function buildManifest(toolSpec, editorial = {}, { media = 'S001_PB_V004.mp4' } = {}) {
  const fps = toolSpec.fps ?? 30;
  const totalFrames = toolSpec.total_frames;
  if (typeof totalFrames !== 'number' || totalFrames <= 0) {
    throw new Error(`invalid total_frames: ${totalFrames}`);
  }
  // All chapters share the same media file — duration is the full file length.
  // The player uses segment boundaries, not duration, to control playback; duration
  // is needed only for schema validation (segments must not exceed it).
  // SPEC-61 D-4: derived from the spec's OWN sections, not the bare declared value — see
  // effectiveTotalFrames.
  const totalDuration = effectiveTotalFrames(toolSpec) / fps;
  // Half-frame tolerance for boundary coincidence, shared by the `loop` and `freeze` branches
  // below (SPEC-61 D-5 extends the `freeze` branch to use the same guard the `loop` branch
  // already had).
  const SEG_EPS = 0.5 / fps;

  // The rest breath: IDLE's authored loop range, when the spec declares one. Null when IDLE
  // is absent or does not loop — BORROWS_IDLE_BREATH then degrades to the old rest-frozen
  // behaviour rather than inventing a loop the art never authored.
  const idleSection = toolSpec.sections.find((s) => s.id === 'IDLE');
  const idleAction = typeof idleSection?.action === 'string'
    ? idleSection.action.toLowerCase() : idleSection?.action;
  const idleBreath = idleSection && idleAction === 'loop'
    ? {
      startFrame: idleSection.loop_start ?? idleSection.start_frame,
      endFrame: idleSection.loop_end ?? idleSection.end_frame,
    }
    : null;

  // SPEC-34 D26: a combined-cut drop carries the carousel's variant segments in this SAME
  // `sections` array, as integer-id entries (1..N, no `action`). They are the other plane
  // of the film — carouselSpecTransform builds them against the same media file — so the
  // main transform passes over them. Unknown STRING ids stay fatal below: a typo'd main
  // section is an authoring error, not a plane.
  const mainSections = toolSpec.sections.filter(
    (section) => isMainSectionId(section.id) || !Number.isInteger(section.id),
  );

  // SPEC-61 D-3/D-7: captures the JUMP section's target frame, once, for the rollChapter
  // check after the chapters are built. Null when the pair authors no jump.
  let jumpInfo = null;

  const chapters = mainSections.map((section) => {
    const id = SECTION_TO_CHAPTER_ID[section.id];
    if (!id) throw new Error(`reelSpecTransform: unknown section id '${section.id}'`);

    const sStart = section.start_frame / fps;
    const sEnd = section.end_frame / fps;

    let segments;
    let autoBeats;

    // Action ids in the spec are inconsistently cased (e.g. IDLE uses "loop" while
    // MOVEMENT uses "Loop") — normalize before dispatching.
    const action = typeof section.action === 'string' ? section.action.toLowerCase() : section.action;

    switch (action) {
      case 'none':
        // Plays through; rests at the neutral end frame.
        segments = [{ type: 'move', start: sStart, end: sEnd }];
        autoBeats = [
          { time: sStart, label: `${id}-approach` },
          { time: sEnd, label: `${id}-neutral` },
        ];
        // …unless the chapter rests ALIVE: append a `detached` hold that loops idle's
        // breath. Its frames live outside this section's span, which reelSchema permits
        // only for flagged loop holds, and useVideoPlayer enters it with a pose-matched
        // seek (the move's end frame and the loop's start are both the neutral pose).
        if (BORROWS_IDLE_BREATH.has(id) && idleBreath) {
          const bStart = idleBreath.startFrame / fps;
          const bEnd = idleBreath.endFrame / fps;
          segments.push({
            type: 'hold',
            detached: true,
            start: bStart,
            end: bEnd,
            loop: {
              start: bStart,
              end: bEnd,
              startFrame: idleBreath.startFrame,
              endFrame: idleBreath.endFrame,
            },
          });
          autoBeats = [
            { time: bStart, label: `${id}-hold` },
            { time: sStart, label: `${id}-approach` },
            { time: sEnd, label: `${id}-neutral` },
          ];
        }
        break;

      case 'freeze': {
        const freezeTime = section.freeze_frame / fps;
        // SPEC-61 D-5: a freeze whose freeze frame IS its own end frame (the mobile
        // CAROUSSEL_ROLL, freeze@3842 == end 3842) would otherwise emit a zero-length exit
        // segment, which reelSchema's `start must be < end` check rejects (F4) — the red
        // panel. Guarded by the same half-frame tolerance the `loop` branch already uses to
        // drop flush segments. Desktop's four freezes are all strictly interior, so this
        // never fires there (pinned by the conformance suite).
        const hasExit = sEnd - freezeTime > SEG_EPS;
        segments = [
          { type: 'move', start: sStart, end: freezeTime },
          { type: 'hold', start: freezeTime, end: freezeTime },
        ];
        if (hasExit) segments.push({ type: 'exit', start: freezeTime, end: sEnd });
        autoBeats = [
          { time: sStart, label: `${id}-approach` },
          { time: freezeTime, label: `${id}-hold` },
        ];
        if (hasExit) autoBeats.push({ time: sEnd, label: `${id}-neutral` });
        break;
      }

      // SPEC-61 D-3: a play-through cut to another point in the SAME file (the mobile
      // CAROUSSEL section's JUMP → the colour roll).
      //
      // 2026-09-16 — a JUMP section may declare `freeze_frame`, and the mobile CAROUSSEL now does.
      // WHY IT MATTERS, measured on the real media: the CAROUSSEL span is not one gesture, it is
      // three. 1359→1489 is the REVEAL (a single watch pulls back into the lit three-piece lineup),
      // 1489 is the lineup HOLD, and 1489→1618 is the lineup COLLAPSING back to the neutral pose —
      // frame 1617 sits 0.0169 from the section's own first frame, i.e. it lands exactly where
      // every main chapter starts. That last third is desktop's CAROUSSEL exit, and it is the
      // authored way OUT of the lineup.
      //
      // Without `freeze_frame` the whole span is one `move`, so entering the collection played the
      // reveal AND the collapse before jumping — on screen: the carousel opens, shuts again, then
      // the roll appears. Reported 2026-09-16 as the sequence "collapsing then jumping, which feels
      // weird". There was also no `exit`, so leaving the lineup for another chapter had no authored
      // way back to neutral at all.
      //
      // With it, a JUMP builds the SAME three segments `freeze` does — the two actions differ only
      // in that a JUMP additionally records where the film cuts to. The route planner then owns
      // which of them plays: entering the roll skips the exit (the jump IS the way on), while
      // leaving the lineup for a main chapter plays it (the collapse IS the way off).
      //
      // Omitted `freeze_frame` keeps the pre-2026-09-16 shape exactly, so any pair that does not
      // declare one is byte-identical.
      case 'jump': {
        jumpInfo = { toFrame: section.jump_to_frame };
        const jumpFrom = Number.isFinite(section.freeze_frame)
          ? section.freeze_frame / fps
          : sEnd;
        // Same half-frame tolerance the `freeze` and `loop` branches use: a freeze flush against
        // the section end emits no exit rather than a zero-length one reelSchema would reject.
        const hasJumpExit = sEnd - jumpFrom > SEG_EPS;
        segments = [
          { type: 'move', start: sStart, end: jumpFrom },
          { type: 'hold', start: jumpFrom, end: jumpFrom },
        ];
        if (hasJumpExit) segments.push({ type: 'exit', start: jumpFrom, end: sEnd });
        autoBeats = [
          { time: sStart, label: `${id}-approach` },
          { time: jumpFrom, label: `${id}-hold` },
        ];
        if (hasJumpExit) autoBeats.push({ time: sEnd, label: `${id}-neutral` });
        break;
      }

      case 'loop': {
        // loop_start/loop_end are optional — a section with no explicit loop points
        // (e.g. IDLE) loops across its entire span.
        const loopStartFrame = section.loop_start ?? section.start_frame;
        const loopEndFrame = section.loop_end ?? section.end_frame;
        const loopStart = loopStartFrame / fps;
        const loopEnd = loopEndFrame / fps;
        // Omit move/exit when loop points sit flush against the section boundaries —
        // producing a zero-length segment (e.g. IDLE looping 0→120 within a 0–120 section)
        // would fail schema validation. A loop that fills the whole section is just a hold.
        segments = [];
        if (loopStart - sStart > SEG_EPS) segments.push({ type: 'move', start: sStart, end: loopStart });
        segments.push({
          type: 'hold',
          start: loopStart,
          end: loopEnd,
          // The spec's frame numbers ride along as integers: the player's frame-accurate
          // wrap (frameBoundaryWatcher) compares presented-frame indices, and re-deriving
          // them from the float seconds would round-trip what the spec states exactly.
          loop: { start: loopStart, end: loopEnd, startFrame: loopStartFrame, endFrame: loopEndFrame },
        });
        if (sEnd - loopEnd > SEG_EPS) segments.push({ type: 'exit', start: loopEnd, end: sEnd });
        autoBeats = [
          { time: loopStart, label: `${id}-hold` },
        ];
        if (loopStart - sStart > SEG_EPS) autoBeats.unshift({ time: sStart, label: `${id}-approach` });
        if (sEnd - loopEnd > SEG_EPS) autoBeats.push({ time: sEnd, label: `${id}-neutral` });
        break;
      }

      default:
        throw new Error(`reelSpecTransform: unknown action '${section.action}' in section '${section.id}'`);
    }

    const overlay = editorial[id] ?? {};

    return {
      id,
      title: overlay.title ?? CHAPTER_TITLES[id] ?? id,
      variant: 'shared',
      media,
      duration: totalDuration,
      segments,
      beats: overlay.beats ?? autoBeats,
      deixis: overlay.deixis ?? {},
      captions: overlay.captions ?? [],
      allowedTransitions: overlay.allowedTransitions ?? [],
    };
  });

  // Dual-outro drops may also carry a RELIGHT strip (top-level `relight`, 2026-08-28): the
  // darkened lineup fading back up to the lit hold — the approach's mirror, authored as a
  // DETACHED tail after the variant plane (it cannot be a section: the main plane is a
  // contiguous run that ends before the slots). It becomes the transit chapter the planner
  // drives through on the way OUT of a slot; its seams are certified per drop by
  // verify_loop_closure.py's RELIGHT section. Malformed frames degrade loudly to "no
  // strip" — a transit corridor must never take the reel down.
  const relight = toolSpec.relight;
  if (relight) {
    const rs = relight.start_frame;
    const re = relight.end_frame;
    if (Number.isFinite(rs) && Number.isFinite(re) && rs >= 0 && rs < re && re <= totalFrames) {
      chapters.push({
        id: 'collection_relight',
        title: CHAPTER_TITLES.collection_relight,
        variant: 'shared',
        media,
        duration: totalDuration,
        segments: [{ type: 'move', start: rs / fps, end: re / fps }],
        beats: [],
        deixis: {},
        captions: [],
        allowedTransitions: [],
      });
    } else {
      console.warn(
        '[SPEC-34] top-level `relight` frames are malformed — the strip is ignored '
        + 'this session (slot leaves fall back to the hard cut):', relight,
      );
    }
  }

  // SPEC-61 D-7: the roll only becomes a real route target when the asset agrees with
  // itself — the CAROUSSEL section's declared jump target must land exactly on the roll
  // chapter's own start frame. A pair that ships a `jump` with no matching chapter (a typo,
  // a future re-cut) degrades to no `rollChapter` rather than a route into nothing; the
  // manifest itself is still valid either way (F5/E25-style tolerance).
  let rollChapter;
  if (jumpInfo) {
    const rollSection = toolSpec.sections.find((s) => SECTION_TO_CHAPTER_ID[s.id] === 'collection_roll');
    const rollBuilt = chapters.some((ch) => ch.id === 'collection_roll');
    if (rollBuilt && rollSection && jumpInfo.toFrame === rollSection.start_frame) {
      rollChapter = 'collection_roll';
    } else {
      console.warn(
        `[SPEC-61] CAROUSSEL declares jump_to_frame ${jumpInfo.toFrame} but no chapter starts `
        + 'there — the roll is disabled this session',
      );
    }
  }

  return {
    version: 1,
    fps,
    ...(rollChapter ? { rollChapter } : {}),
    // Same-origin by default. Point REACT_APP_REEL_MEDIA_BASE at an Azure container (WITH the
    // trailing slash — reelSchema's mediaUrl() is a plain concat) to stream chapter media the
    // way the intro film already streams: the reel is expected to grow, and large media should
    // live off-bundle. <video> playback needs no CORS; the boot HEAD-check in useChapterManifest
    // already exempts absolute URLs by design.
    mediaBase: process.env.REACT_APP_REEL_MEDIA_BASE || '/reel/',
    // TRAVERSAL rate for `move`/`exit` only — `useVideoPlayer` keeps loop holds at 1× so the
    // idle breath is unaffected, and the intro film (chapter.audio) is likewise exempt.
    //
    // 1.5 — ratified ON SCREEN 2026-08-27: the team review called the 1× pacing laggy, a 2.0
    // trial the same day read as too fast, and 1.5 split the difference once the WORST of
    // the lag turned out to be boundary artifacts fixed in the player instead (the 3 fps
    // ease-floor crawl into freeze-holds, and the mid-breath pin teleport on no-exit loop
    // departures — see useVideoPlayer.js, same date).
    //
    // The knob is safe in this range on the current master (combined V004 measured
    // 2026-08-28: 104.7 MB / 111.8 s ≈ 7.5 Mbps → ~11 Mbps sustained at 1.5×, ordinary —
    // same bitrate as the split-era 48.6 MB / 53.9 s file it replaced). The historical
    // freeze was 5× over the 31 Mbps original (~155 Mbps demand, docs/reel-retime.md) — so
    // before raising this further, re-measure the live master's bitrate (Content-Length ÷
    // duration), prefer the piecewise retime for anything aggressive (speed in the FILE,
    // not the player), and QA for `[Projectionist] media starved` console warnings on a
    // throttled connection. stallGuard + rVFC-owned boundaries are the belt if a heavy
    // master ever comes back.
    playbackRate: 1.5,
    boot: 'idle',
    home: 'idle',
    chapters,
  };
}
